import React, { useState } from "react";
import { toast } from "react-toastify";

const STEP_LABELS = ["Details", "Payment", "Confirm"];
const PAY_METHODS = [
  { id: "upi", ico: "📲", name: "UPI", desc: "Google Pay, PhonePe, BHIM" },
  {
    id: "card",
    ico: "💳",
    name: "Credit / Debit Card",
    desc: "Visa, Mastercard, RuPay",
  },
  {
    id: "netbanking",
    ico: "🏦",
    name: "Net Banking",
    desc: "All major banks supported",
  },
  { id: "wallet", ico: "👛", name: "Wallets", desc: "Paytm, MobiKwik & more" },
];
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const RAZORYPAY_KEY = process.env.REACT_APP_RAZORYPAY_KEY;

/* ── Order Summary (reused on both steps) ── */
function OrderSummary({ cart, mrpMap }) {
  const cartKeys = Object.keys(cart);
  const { base, saved } = cartKeys.reduce(
    (acc, id) => {
      const itemBase = cart[id].price;
      acc.base += itemBase * cart[id].qty;
      if (mrpMap[id]) acc.saved += (mrpMap[id] - itemBase) * cart[id].qty;
      return acc;
    },
    { base: 0, saved: 0 },
  );
  const gstAmount = Math.round(base * 0.05);
  const grandTotal = base + gstAmount;

  return (
    <div className="order-summary">
      <div className="os-label">Order Summary</div>
      {cartKeys.map((id) => {
        const item = cart[id];
        return (
          <div className="os-row os-item-row" key={id}>
            <span className="os-item-name">
              {item.name}
              {item.nameSub ? ` ${item.nameSub}` : ""}
              {item.qty > 1 ? ` ×${item.qty}` : ""}
            </span>
            <span className="os-item-price">₹{(item.price * item.qty).toLocaleString("en-IN")}</span>
          </div>
        );
      })}
      {/* <div className="os-divider" /> */}
      <div className="os-row">
        <span>Price</span>
        <span>₹{base.toLocaleString("en-IN")}</span>
      </div>
      <div className="os-save">
        You save ₹{saved.toLocaleString("en-IN")} vs Price
      </div>
      <div className="os-row os-gst">
        <span>GST (5%)</span>
        <span>+ ₹{gstAmount.toLocaleString("en-IN")}</span>
      </div>
      {/* <div className="os-divider" /> */}
      <div className="os-row os-total">
        <span><b>Grand Total</b></span>
        <span><b>₹{grandTotal.toLocaleString("en-IN")}</b></span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   ENROLL MODAL
══════════════════════════════════════════════ */
export default function EnrollModal({
  open,
  cart,
  mrpMap,
  onClose,
  onPaymentSuccess,
}) {
  const [step, setStep] = useState(1);
  const [payMethod, setPayMethod] = useState("upi");
  const [memberDetailsId, setMemberDetailsId] = useState(null);
  const [paymentRequestId, setPaymentRequestId] = useState(null);

  // Compute grand total for Pay Now button
  const cartBase = Object.keys(cart).reduce(
    (sum, id) => sum + cart[id].price * cart[id].qty,
    0,
  );
  const cartGst = Math.round(cartBase * 0.05);
  const cartGrandTotal = cartBase + cartGst;
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    memid: "",
    city: "",
    address: "",
  });
  const [errors, setErrors] = useState({});

  // Reset to step 1 whenever the modal opens
  React.useEffect(() => {
    if (open) {
      setStep(1);
      setErrors({});
    }
  }, [open]);

  /* ── Close handler — also resets step ── */
  function handleClose() {
    setStep(1);
    setErrors({});
    onClose();
  }

  function handleChange(e) {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: false }));
  }

  function validate() {
    const rules = {
      name: (v) => v.trim().length > 1,
      mobile: (v) => /^[6-9]\d{9}$/.test(v.trim()),
      email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()),
      memid: (v) => v.trim().length > 2,
      city: (v) => v.trim().length > 1,
      address: (v) => v.trim().length > 5,
    };
    const newErrors = {};
    Object.keys(rules).forEach((k) => {
      if (!rules[k](formData[k])) newErrors[k] = true;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function goNext() {
    if (step === 1) {
      if (validate()) {
        const isSuccess = await sendMemberDetails();
        if (isSuccess) {
          setStep(2);
        }
      }
    } else if (step === 2) {
      const isSuccess = await sendPaymentRequest();
      if (isSuccess) {
        launchRazorpay();
      }
    }
  }

  async function sendMemberDetails() {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      "name": formData["name"],
      "mobile": formData["mobile"],
      "email": formData["email"],
      "city": formData["city"],
      "iadvl_membership_no":formData["memid"],
      "address":formData["address"]
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
     
      const response = await fetch(`${API_BASE_URL}/member_details`, requestOptions);
      const text = await response.text();

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error("Failed to parse member_details response", e, text);
        toast.error("Something went wrong. Please try again.");
        return false;
      }

      const status = data?.data?.status;
      if (status === "success") {
        const memberId = data?.data?.data?.member_details_id;
        if (memberId) {
          setMemberDetailsId(memberId);
          try {
            sessionStorage.setItem("iadvl_member_details_id", String(memberId));
          } catch (_) {}
        }
        const successMessage =
          data?.data?.message || data?.message || "Member details added successfully";
        toast.success(successMessage);
        return true;
      } else {
        const errorMessage =
          data?.data?.message || data?.message || "Unable to add member details. Please try again.";
        toast.error(errorMessage);
        return false;
      }
    } catch (error) {
      console.error("member_details API error", error);
      toast.error("Network error. Please check your connection and try again.");
      return false;
    }
  }

  async function sendPaymentRequest() {
    const memberId = memberDetailsId || (() => {
      try {
        return sessionStorage.getItem("iadvl_member_details_id");
      } catch (_) {
        return null;
      }
    })();

    if (!memberId) {
      toast.error("Member details not found. Please re-submit your details.");
      return false;
    }

    const paymentTypeMap = {
      upi: "upi",
      card: "credit",
      netbanking: "netbanking",
      wallet: "wallet",
    };

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      member_id: String(memberId),
      purchases: Object.entries(cart).map(([id, item]) => ({
        id,
        sku:item.sku,
        title: item.name + (item.nameSub ? " " + item.nameSub : ""),
        qty: item.qty,
        amount: item.price,
        afterGST: item.price + Math.round(item.price * 0.05),
        type: id.startsWith("a") ? "addon" : "primary",
      })),
      amount: String(cartGrandTotal),
      payment_type: paymentTypeMap[payMethod] || "credit",
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(
        `${API_BASE_URL}/payment_request`,
        requestOptions,
      );
      const text = await response.text();

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error("Failed to parse payment_request response", e, text);
        toast.error("Something went wrong. Please try again.");
        return false;
      }

      const status = data?.data?.status;
      if (status === "success") {
        const prId = data?.data?.data?.paymente_request_id;
        if (prId) {
          setPaymentRequestId(prId);
          try {
            sessionStorage.setItem("iadvl_payment_request_id", String(prId));
          } catch (_) {}
        }
        const successMessage =
          data?.data?.message || data?.message || "Payment request created successfully";
        toast.success(successMessage);
        return true;
      } else {
        const errorMessage =
          data?.data?.message || data?.message || "Unable to create payment request. Please try again.";
        toast.error(errorMessage);
        return false;
      }
    } catch (error) {
      console.error("payment_request API error", error);
      toast.error("Network error. Please check your connection and try again.");
      return false;
    }
  }

  function launchRazorpay() {
    const memberId =
      memberDetailsId ||
      (() => {
        try {
          return sessionStorage.getItem("iadvl_member_details_id");
        } catch (_) {
          return null;
        }
      })();
    const prId =
      paymentRequestId ||
      (() => {
        try {
          return sessionStorage.getItem("iadvl_payment_request_id");
        } catch (_) {
          return null;
        }
      })();

    const options = {
      key: RAZORYPAY_KEY, // ← Replace with your Razorpay Key ID
      amount: cartGrandTotal * 100,     // Grand Total (base + 5% GST) in paise
      currency: "INR",
      name: "IADVL × GOQii Wellness",
      description: `Price ₹${cartBase.toLocaleString("en-IN")} + GST ₹${cartGst.toLocaleString("en-IN")} = ₹${cartGrandTotal.toLocaleString("en-IN")}`,
      prefill: {
        name: formData.name,
        email: formData.email,
        contact: formData.mobile,
      },
      notes: {
        membership_id: formData.memid,
        member_details_id: memberId || "",
        payment_request_id: prId || "",
        address: formData.address,
        plans: Object.values(cart)
          .map((c) => `${c.name} ×${c.qty}`)
          .join(", "),
      },
      theme: { color: "#E8560A" },
      method: {
        upi: payMethod === "upi",
        card: payMethod === "card",
        netbanking: payMethod === "netbanking",
        wallet: payMethod === "wallet",
        emi: false,
      },
      handler: (response) =>
        onPaymentSuccess(response, {
          ...formData,
          member_details_id: memberId || memberDetailsId || null,
          payment_request_id: prId || paymentRequestId || null,
        }),
      modal: { ondismiss: () => { } },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => { });
      rzp.open();
    } catch (e) {
      // Demo fallback when Razorpay is not loaded / key is invalid
      onPaymentSuccess(
        {
          razorpay_payment_id: "pay_demo_" + Date.now(),
          razorpay_order_id:
            "order_DEMO" +
            Math.random().toString(36).substr(2, 8).toUpperCase(),
        },
        formData,
      );
    }
  }

  /* ── Step node / label CSS class helpers ── */
  const nodeClass = (i) =>
    `step-node  ${i < step ? "done" : i === step ? "active" : "pending"}`;
  const labelClass = (i) =>
    `step-label ${i < step ? "done" : i === step ? "active" : "pending"}`;

  return (
    <div className={`enroll-modal${open ? " open" : ""}`}>
      <div className="enroll-box">
        {/* ════════════════════════════════════
            MODAL HEADER  (title + close btn)
        ════════════════════════════════════ */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 10px 0",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              fontFamily: "var(--font-disp)",
              fontSize: 13,
              fontWeight: 400,
              color: "var(--ink-3)",
              letterSpacing: ".2px",
            }}
          >
            {/* Enrolment Form */}
          </div>

          {/* ── Close Button ── */}
          <button
            onClick={handleClose}
            aria-label="Close enrolment form"
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "none",
              background: "var(--bg)",
              color: "var(--ink-3)",
              fontSize: 15,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background .15s, color .15s",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(192,57,43,.1)";
              e.currentTarget.style.color = "var(--red)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--bg)";
              e.currentTarget.style.color = "var(--ink-3)";
            }}
          >
            ✕
          </button>
        </div>

        {/* ════════════════════════════════════
            STEP PROGRESS BAR
        ════════════════════════════════════ */}
        <div className="step-bar">
          {[1, 2, 3].map((i, idx) => (
            <React.Fragment key={i}>
              <div className={nodeClass(i)}>{i < step ? "✓" : i}</div>
              {idx < 2 && (
                <div className="step-line">
                  <div className={`step-line-fill${i < step ? " done" : ""}`} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="step-labels">
          {STEP_LABELS.map((lbl, idx) => (
            <span key={lbl} className={labelClass(idx + 1)}>
              {lbl}
            </span>
          ))}
        </div>

        {/* ════════════════════════════════════
            SCROLLABLE BODY
        ════════════════════════════════════ */}
        <div className="enroll-scroll">
          {/* ── STEP 1 : Member Details ── */}
          {step === 1 && (
            <div>
              <div className="enroll-title">Member Details</div>
              <div className="enroll-sub">
                Please fill in your details. These will be used for your
                enrolment and GOQii account setup.
              </div>

              <OrderSummary cart={cart} mrpMap={mrpMap} />

              <div className="form-grid">
                <div className={`field${errors.name ? " has-err" : ""}`}>
                  <label>Full Name *</label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Dr. Priya Sharma"
                    autoComplete="name"
                    className={errors.name ? "err" : ""}
                  />
                  <span className="err-msg">Please enter your full name</span>
                </div>
                <div className={`field${errors.mobile ? " has-err" : ""}`}>
                  <label>Mobile Number *</label>
                  <input
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="9XXXXXXXXX"
                    maxLength={10}
                    autoComplete="tel"
                    className={errors.mobile ? "err" : ""}
                  />
                  <span className="err-msg">
                    Enter a valid 10-digit mobile number
                  </span>
                </div>
              </div>

              <div className="form-grid full">
                <div className={`field${errors.email ? " has-err" : ""}`}>
                  <label>Email Address *</label>
                  <input
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="drpriya@email.com"
                    autoComplete="email"
                    className={errors.email ? "err" : ""}
                  />
                  <span className="err-msg">Enter a valid email address</span>
                </div>
              </div>

              <div className="form-grid">
                <div className={`field${errors.memid ? " has-err" : ""}`}>
                  <label>IADVL Membership Number *</label>
                  <input
                    name="memid"
                    value={formData.memid}
                    onChange={handleChange}
                    placeholder="IADVL-XXXXX"
                    className={errors.memid ? "err" : ""}
                  />
                  <span className="err-msg">Membership number is required</span>
                </div>
                <div className={`field${errors.city ? " has-err" : ""}`}>
                  <label>City *</label>
                  <input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Mumbai"
                    autoComplete="address-level2"
                    className={errors.city ? "err" : ""}
                  />
                  <span className="err-msg">Please enter your city</span>
                </div>
              </div>

              <div className="form-grid full" style={{ paddingBottom: 4 }}>
                <div className={`field${errors.address ? " has-err" : ""}`}>
                  <label>Clinic / Delivery Address *</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Flat / Clinic No., Street, Area, Pincode"
                    autoComplete="street-address"
                    className={errors.address ? "err" : ""}
                  />
                  <span className="err-msg">Please enter your address</span>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 2 : Payment ── */}
          {step === 2 && (
            <div>
              <div className="enroll-title">Payment</div>
              <div className="enroll-sub">
                Choose your preferred payment method. You'll be redirected to
                Razorpay's secure checkout.
              </div>

              {/* Price Summary */}
              <div className="price-summary-box">
                <div className="ps-heading">Order Summary</div>
                <div className="ps-row">
                  <span>Price</span>
                  <span>₹{cartBase.toLocaleString("en-IN")}</span>
                </div>
                <div className="ps-row ps-gst">
                  <span>GST (5%)</span>
                  <span>+ ₹{cartGst.toLocaleString("en-IN")}</span>
                </div>
                <div className="ps-divider" />
                <div className="ps-row ps-grand">
                  <span><b>Grand Total</b></span>
                  <span><b>₹{cartGrandTotal.toLocaleString("en-IN")}</b></span>
                </div>
              </div>

              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  color: "var(--ink-4)",
                  marginBottom: 12,
                }}
              >
                Select Payment Method
              </div>

              <div className="pay-methods">
                {PAY_METHODS.map((m) => (
                  <div
                    key={m.id}
                    className={`pay-tile${payMethod === m.id ? " selected" : ""}`}
                    onClick={() => setPayMethod(m.id)}
                  >
                    <span className="pt-ico">{m.ico}</span>
                    <div>
                      <div className="pt-name">{m.name}</div>
                      <div className="pt-desc">{m.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pay-notice" style={{ marginTop: 14 }}>
                <strong>Secure payment powered by Razorpay.</strong> Your
                payment information is encrypted and never stored on our
                servers. A GST invoice will be emailed to you after successful
                payment.
              </div>
            </div>
          )}
        </div>

        {/* ════════════════════════════════════
            FOOTER  (Back / Continue / Pay)
        ════════════════════════════════════ */}
        <div className="enroll-foot">
          {step > 1 && (
            <button className="back-btn" onClick={() => setStep((s) => s - 1)}>
              ← Back
            </button>
          )}
          <button className="next-btn" onClick={goNext}>
            {step === 2
              ? `Pay ₹${cartGrandTotal.toLocaleString("en-IN")} Now →`
              : "Continue →"}
          </button>
        </div>
      </div>
    </div>
  );
}
